import React, { forwardRef } from 'react';
import { ArrowRight, Button, CheckSuccess, ErrorCrossIcon, Typography } from '../../index';

type Link = {
	text: string;
	url?: string;
	onClick?: () => void;
};

enum ESize {
	small = 'base',
	medium = 'lgEven',
	large = 'xlEven',
}

export type Hint = {
	tip?: string | React.ReactNode;
	message: string | React.ReactNode;
	link?: Link;
};

enum EPaddingSize {
	small = 'px-3',
	large = 'px-4',
}

interface IHintMessageProps {
	hints: Hint[];
	bottomText?: string;
	className?: string;
	contentClassName?: string;
	size?: keyof typeof ESize;
	padding?: keyof typeof EPaddingSize;
	variant?: 'info' | 'error' | 'success';
}

enum EHintMessageType {
	info = 'bg-lightBlue',
	error = 'bg-lightRed',
	success = 'bg-lightGreen',
}

type Ref = HTMLDivElement;

const renderLink = (link) => {
	const { onClick } = link;
	return link.url ? (
		<a
			href={link.url}
			target="_blank"
			className="pt-2 flex mx-auto text-center items-center gap-1.5 font-bold no-underline text-gray-200 hover:text-black">
			<ArrowRight fill="currentColor" size="20" />
			{link.text}
		</a>
	) : (
		<Button
			size="sm"
			className="mt-2 gap-1.5 bg-transparent group"
			style={{ padding: 0 }}
			variant="tertiary"
			onClick={onClick}>
			<ArrowRight className="inline-flex self-center group-hover:[&>*]:stroke-gray-100" size="20" />
			<span className="whitespace-nowrap">{link.text}</span>
		</Button>
	);
};

export const HintMessage = forwardRef<Ref, IHintMessageProps>((props, ref) => {
	const {
		hints,
		bottomText,
		className,
		contentClassName,
		size = 'small',
		padding = 'large',
		variant = 'info',
	}: IHintMessageProps = props;
	const hintIcons = {
		error: <ErrorCrossIcon className="shrink-0 inline-flex self-baseline" />,
		success: <CheckSuccess className="shrink-0 inline-flex self-baseline" />,
	};

	return (
		<>
			<div
				ref={ref}
				role={variant === 'error' ? 'alert' : 'note'}
				className={`${EHintMessageType[variant]} rounded-lg py-3.5 border-2 border-solid border-black border-opacity-[0.04] ${EPaddingSize[padding]} ${className}`}>
				{hints.map(({ tip, message, link = undefined }, index) => (
					<React.Fragment key={index}>
						<Typography
							component="p"
							className={`${variant !== 'info' && 'flex items-center gap-1.5'} ${contentClassName}`}
							color="title"
							size={ESize[size]}>
							{tip ? (
								<>
									<Typography variant="title" color="title" size={ESize[size]}>
										{tip}
									</Typography>{' '}
								</>
							) : (
								variant !== 'info' && hintIcons[variant]
							)}
							{message}
						</Typography>
						{link && renderLink(link)}
					</React.Fragment>
				))}
			</div>
			{bottomText && (
				<Typography className="pt-2 py-3.5 px-4" component="p" size="sm">
					{bottomText}
				</Typography>
			)}
		</>
	);
});

HintMessage.displayName = 'HintMessage';
